import { z } from 'zod';
import { AI_CALLER_MAX_CREDITS } from '../aiPricing';
import { IsoDateTimeSchema, UuidSchema } from './common';

/** Spec §13 */
export const INSIGHT_TYPES = ['campaign', 'operations', 'retention', 'data_quality'] as const;
export type InsightType = (typeof INSIGHT_TYPES)[number];

export const INSIGHT_CONFIDENCE = ['low', 'medium', 'high'] as const;
export type InsightConfidence = (typeof INSIGHT_CONFIDENCE)[number];

export const INSIGHT_STATUSES = ['new', 'dismissed', 'saved', 'converted_to_draft'] as const;
export type InsightStatus = (typeof INSIGHT_STATUSES)[number];

/**
 * Deterministic analyzers (insights module). Each produces at most one open card at a time; the key
 * is the card's stable identity (`insight_recommendations.fingerprint`).
 */
export const INSIGHT_KEYS = [
  'inactive_customers',
  'birthday_opportunity',
  'review_requests_missing',
  'review_funnel_change',
  'feedback_topic',
  'sms_cost_saving',
  'campaign_results',
  'campaign_delivery_issues',
  'consent_unknown',
  'unmatched_events',
  'possible_duplicates',
  /** Forms: positive answers not followed up for a week. */
  'form_follow_up',
  /** Events: invited people who have not answered, event within a week. */
  'event_no_reply',
] as const;
export type InsightKey = (typeof INSIGHT_KEYS)[number];

export const INSIGHT_UNITS = ['count', 'percent', 'cents', 'days', 'sms_parts'] as const;
export type InsightUnit = (typeof INSIGHT_UNITS)[number];

/**
 * One number behind a card: what was counted (`metric`, an i18n key under `insights:metrics`),
 * over which time window and from which data source. `ref` is the stable metric reference that
 * also appears in `supportingMetricRefs`.
 */
export const InsightEvidenceSchema = z.object({
  metric: z.string(),
  value: z.union([z.number(), z.string()]),
  window: z.string().optional(),
  source: z.string(),
  /** Stable metric reference, e.g. `contacts:inactive:60-179d`. */
  ref: z.string().optional(),
  unit: z.enum(INSIGHT_UNITS).optional(),
  /** Window length in days (absent for point-in-time counts). */
  windowDays: z.number().int().optional(),
  /** Same metric over the previous window (trend cards). */
  previous: z.number().optional(),
  /** Free display parameters (e.g. the feedback topic key). */
  params: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
});
export type InsightEvidence = z.infer<typeof InsightEvidenceSchema>;

/** A business note the card cites. Notes are context someone reported — never verified facts. */
export const InsightNoteRefSchema = z.object({
  id: UuidSchema,
  title: z.string().nullable(),
  excerpt: z.string(),
  verification: z.enum(['user_reported', 'confirmed', 'ai_inferred', 'rejected']),
  createdAt: IsoDateTimeSchema.nullable(),
  /** The note was deleted or hidden from insights after the card was written. */
  unavailable: z.boolean().default(false),
});
export type InsightNoteRef = z.infer<typeof InsightNoteRefSchema>;

export const INSIGHT_ACTIONS = ['campaign_draft', 'segment_draft', 'link'] as const;

/** What "convert to draft" will prepare (nothing is ever sent). */
export const InsightActionSchema = z.object({
  kind: z.enum(INSIGHT_ACTIONS),
  /** Campaign drafts: channel and the audience the draft would address. */
  channel: z.enum(['sms', 'email']).optional(),
  audienceSize: z.number().int().optional(),
  /** In-app link (settings page, contacts filter…), for `link` actions. */
  href: z.string().optional(),
  /** i18n key under `insights:actions`. */
  labelKey: z.string(),
});
export type InsightAction = z.infer<typeof InsightActionSchema>;

export const InsightRecommendationSchema = z.object({
  id: UuidSchema,
  type: z.enum(INSIGHT_TYPES),
  title: z.string(),
  explanation: z.string(),
  evidence: z.array(InsightEvidenceSchema),
  supportingMetricRefs: z.array(z.string()),
  supportingNoteRefs: z.array(z.string()),
  generatedAt: z.string(),
  expiresAt: z.string(),
  confidence: z.enum(INSIGHT_CONFIDENCE),
  proposedSegmentId: UuidSchema.nullable(),
  proposedCampaignDraftId: UuidSchema.nullable(),
  status: z.enum(INSIGHT_STATUSES),
  /** Additive (insights module). */
  key: z.string().optional(),
  priority: z.number().int().optional(),
  action: InsightActionSchema.nullable().optional(),
  notes: z.array(InsightNoteRefSchema).optional(),
  /** Where to open the proposed draft (never an action that sends). */
  draftHref: z.string().nullable().optional(),
  expired: z.boolean().optional(),
  decidedAt: z.string().nullable().optional(),
  runId: UuidSchema.nullable().optional(),
  /** How the text was written: by the model (validated) or by the built-in template. */
  phrasing: z.enum(['model', 'template']).optional(),
  engineVersion: z.string().optional(),
});
export type InsightRecommendation = z.infer<typeof InsightRecommendationSchema>;

// ─── settings, runs, API ─────────────────────────────────────────────────────

export const INSIGHT_RUN_OUTCOMES = [
  'completed',
  'no_new_data',
  'skipped_opt_out',
  'skipped_disabled',
  'skipped_budget',
  'skipped_plan',
  'skipped_cap',
  'skipped_recent',
  'failed',
] as const;
export type InsightRunOutcome = (typeof INSIGHT_RUN_OUTCOMES)[number];

export const InsightRunSummarySchema = z.object({
  at: IsoDateTimeSchema,
  outcome: z.enum(INSIGHT_RUN_OUTCOMES),
  trigger: z.enum(['schedule', 'manual']),
  runId: UuidSchema.nullable(),
  cardsCreated: z.number().int(),
  cardsRefreshed: z.number().int(),
  draftsCreated: z.number().int(),
  creditsUsed: z.number().int(),
  /** Newest data timestamp the run looked at (new-data watermark). */
  dataWatermark: IsoDateTimeSchema.nullable(),
});
export type InsightRunSummary = z.infer<typeof InsightRunSummarySchema>;

export const InsightSettingsSchema = z.object({
  /** The business turned scheduled insights off. Existing cards stay readable. */
  optOut: z.boolean(),
});
export type InsightSettings = z.infer<typeof InsightSettingsSchema>;
export const UpdateInsightSettingsSchema = InsightSettingsSchema.strict();

export const INSIGHT_LIST_VIEWS = ['open', 'saved', 'history'] as const;
export const InsightListQuerySchema = z.object({
  view: z.enum(INSIGHT_LIST_VIEWS).default('open'),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export const InsightListResponseSchema = z.object({
  items: z.array(InsightRecommendationSchema),
  counts: z.object({ open: z.number().int(), saved: z.number().int() }),
  settings: InsightSettingsSchema,
  lastRun: InsightRunSummarySchema.nullable(),
  /** Scheduled runs are switched on platform-wide (Super Admin). */
  scheduleEnabled: z.boolean(),
});
export type InsightListResponse = z.infer<typeof InsightListResponseSchema>;

export const ConvertInsightResponseSchema = z.object({
  insight: InsightRecommendationSchema,
  draft: z.object({
    kind: z.enum(['campaign_draft', 'segment_draft']),
    id: UuidSchema,
    href: z.string(),
    /** An existing draft was returned (idempotent). */
    replayed: z.boolean(),
  }),
});
export type ConvertInsightResponse = z.infer<typeof ConvertInsightResponseSchema>;

export const RunInsightsResponseSchema = z.object({ run: InsightRunSummarySchema });

/** Super Admin knobs (platform_config `insights.schedule`). */
export const InsightsScheduleConfigSchema = z.object({
  enabled: z.boolean().default(true),
  /** Local (tenant timezone) hour window in which scheduled runs may start, [start, end). */
  windowStartHour: z.number().int().min(0).max(23).default(6),
  windowEndHour: z.number().int().min(1).max(24).default(10),
  /** Minimum hours between two runs of the same tenant (scheduled or manual). */
  minHoursBetweenRuns: z
    .number()
    .int()
    .min(1)
    .max(24 * 30)
    .default(20),
  /** Hard cap of AI credits per run. */
  maxCreditsPerRun: z
    .number()
    .int()
    .min(AI_CALLER_MAX_CREDITS.insightsMinPerRun)
    .max(200)
    .default(12),
  /** Runs per tenant per calendar month (scheduled + manual). */
  maxRunsPerMonth: z.number().int().min(0).max(100).default(31),
  /** Scheduled runs are skipped when the AI wallet would drop below this (keeps credits for chat). */
  minWalletCredits: z.number().int().min(0).max(10_000).default(25),
  /** Campaign drafts the scheduled run may prepare on its own (top cards only). */
  autoDraftsPerRun: z.number().int().min(0).max(3).default(1),
  cardTtlDays: z.number().int().min(1).max(90).default(14),
  /** A dismissed card's analyzer stays quiet this long. */
  dismissCooldownDays: z.number().int().min(0).max(180).default(21),
});
export type InsightsScheduleConfig = z.infer<typeof InsightsScheduleConfigSchema>;
export const UpdateInsightsScheduleConfigSchema = InsightsScheduleConfigSchema.partial().strict();
